import {ToastErrors} from '@/toaster.ts';
import {Button, IconName, Size, Tooltip} from '@blueprintjs/core';
import {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';

export type TextProviderFn = () => string;

interface Props {
	copyText: string | TextProviderFn,
	tooltip: string,
	size?: Size,
	icon?: IconName,
}

export default function CopyButton({
	copyText,
	tooltip,
	icon = 'duplicate',
	size = Size.SMALL,
}: Props): ReactElement {
	const [copied, setCopied] = useState(false);
	const timer = useRef(-1);

	const text = useMemo(() => {
		if (typeof copyText === 'function')
			return copyText();

		return copyText;
	}, [copyText]);

	const onClick = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (error) {
			ToastErrors.fromError(error, 'Could not copy to clipboard: ');
			return;
		}

		setCopied(true);

		if (timer.current >= 0)
			window.clearTimeout(timer.current);

		timer.current = window.setTimeout(() => {
			setCopied(false);
		}, 2_000);
	}, [text]);

	useEffect(() => {
		return () => {
			if (timer.current >= 0) {
				window.clearTimeout(timer.current);
				timer.current = -1;
			}
		};
	}, []);

	return (
		<Tooltip content={copied ? 'Copied!' : tooltip} intent={copied ? 'success' : undefined}>
			<Button icon={icon} size={size} onClick={onClick} variant="minimal" />
		</Tooltip>
	);
}
